import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  CheckCircle2,
  Clock,
  CloudOff,
  Download,
  Filter,
  ListChecks,
  NotebookPen,
  Printer,
  RefreshCw,
  Search,
  Upload,
  UserCheck,
  Users,
  XCircle,
} from "lucide-react";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';


/**
 * Attendance.jsx
 * ------------------------------------------------------------
 * Pure-frontend UI for taking attendance with dummy data.
 * - Homeroom or Period mode
 * - Class & date selector
 * - Search & quick bulk actions
 * - One-tap status chips (Present / Absent / Late / Excused)
 * - Live counters + faux Save
 * - Responsive, TailwindCSS
 * ------------------------------------------------------------
 * Assumptions: Tailwind + lucide-react already available (same as your Dashboard.jsx).
 */

const STATUS = {
  PRESENT: "present",
  ABSENT: "absent",
  LATE: "late",
  EXCUSED: "excused",
};

// Helper for percentages
const pct = (num, den) => (den ? Math.round((num / den) * 100) : 0);

// Week helpers (Mon–Fri) based on selected date
const getWeekDates = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay(); // 0 Sun ... 6 Sat
  const diffToMon = (day === 0 ? -6 : 1 - day); // shift so Monday is start
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMon);
  const days = Array.from({ length: 5 }).map((_, i) => {
    const x = new Date(monday);
    x.setDate(monday.getDate() + i);
    return x;
  });
  return days;
};

const fmtDay = (dt) => dt.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });

const abbrDay = (dt) => dt.toLocaleDateString(undefined, { weekday: "short" });
  // 0% -> red, 100% -> green for heatmap blocks
  const colorFromPct = (p) => {
    const hue = Math.max(0, Math.min(120, Math.round((p / 100) * 120)));
    return `hsl(${hue} 70% 60%)`;
  };

const statusMeta = {
  [STATUS.PRESENT]: { label: "Present", cls: "bg-green-50 text-green-700 border-green-200" },
  [STATUS.ABSENT]: { label: "Absent", cls: "bg-red-50 text-red-700 border-red-200" },
  [STATUS.LATE]: { label: "Late", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  [STATUS.EXCUSED]: { label: "Excused", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

// ---------- Dummy Data -------------
const dummyClasses = [
  { id: "c1", uuid: "uuid-c1", class_name: "JSS1A", student_count: 29 },
  { id: "c2", uuid: "uuid-c2", class_name: "JSS1B", student_count: 31 },
  { id: "c3", uuid: "uuid-c3", class_name: "JSS2A", student_count: 28 },
];

const dummyStudents = {
  "uuid-c1": Array.from({ length: 29 }).map((_, i) => ({
    id: `s1-${i + 1}`,
    admission_no: `J1A/${100 + i}`,
    firstname: ["Amara", "Bola", "Chidi", "Dare", "Efe", "Fatima", "Gbenga", "Hauwa", "Ife", "Joy", "Kemi", "Lekan", "Mayowa", "Ngozi", "Ola", "Peace", "Qudus", "Rukayat", "Samuel", "Tola", "Uche", "Victoria", "Wale", "Xavier", "Yemi", "Zainab", "Aisha", "Basit", "Chinwe"][i % 29],
    lastname: ["Abiodun", "Bello", "Chukwu", "Dada", "Ekanem"][i % 5],
    status: STATUS.PRESENT,
    note: "",
  })),
  "uuid-c2": Array.from({ length: 31 }).map((_, i) => ({
    id: `s2-${i + 1}`,
    admission_no: `J1B/${100 + i}`,
    firstname: ["Ada", "Bisi", "Chika", "Damilola", "Emeka"][i % 5],
    lastname: ["Okafor", "Ogunleye", "Umar", "Olawale", "Mohammed"][i % 5],
    status: STATUS.PRESENT,
    note: "",
  })),
  "uuid-c3": Array.from({ length: 28 }).map((_, i) => ({
    id: `s3-${i + 1}`,
    admission_no: `J2A/${100 + i}`,
    firstname: ["Abdul", "Bella", "Chima", "Deborah", "Ezekiel"][i % 5],
    lastname: ["Udo", "Ojo", "Aliyu", "Okoro", "Babangida"][i % 5],
    status: STATUS.PRESENT,
    note: "",
  })),
};

// ------- PDF Components (no popups) -------
const pdfStyles = StyleSheet.create({
  page: { padding: 18, fontSize: 10, fontFamily: 'Helvetica' },
  header: { marginBottom: 8 },
  title: { fontSize: 14, fontWeight: 'bold', marginBottom: 4 },
  meta: { fontSize: 9, marginBottom: 6 },
  row: { flexDirection: 'row' },
  cell: { borderWidth: 1, borderColor: '#000', padding: 3, fontSize: 8, justifyContent: 'center' },
  th: { backgroundColor: '#e5e7eb', fontWeight: 'bold' },
  legend: { fontSize: 8, marginBottom: 6 },
});

// PDF for current register
const AttendanceRegisterDoc = ({ cls, date, slot, rows, statusMeta }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.header}>
        <Text style={pdfStyles.title}>Attendance Register</Text>
        <Text style={pdfStyles.meta}>Class: {cls}   Date: {date}   Slot: {slot}</Text>
      </View>

      <View style={pdfStyles.row}>
        {["#", "Student Name", "Admission No", "Status", "Note"].map((h, i) => (
          <View key={i} style={[pdfStyles.cell, pdfStyles.th, { flex: [0.6, 2.2, 1.4, 1.2, 2.4][i] }]}>
            <Text>{h}</Text>
          </View>
        ))}
      </View>

      {rows.map((s, idx) => (
        <View key={s.id} style={pdfStyles.row}>
          <View style={[pdfStyles.cell, { flex: 0.6 }]}><Text>{idx + 1}</Text></View>
          <View style={[pdfStyles.cell, { flex: 2.2 }]}><Text>{`${s.firstname} ${s.lastname}`}</Text></View>
          <View style={[pdfStyles.cell, { flex: 1.4 }]}><Text>{s.admission_no}</Text></View>
          <View style={[pdfStyles.cell, { flex: 1.2 }]}><Text>{statusMeta[s.status]?.label || s.status}</Text></View>
          <View style={[pdfStyles.cell, { flex: 2.4 }]}><Text>{s.note || ""}</Text></View>
        </View>
      ))}

      <View style={{ marginTop: 6 }}>
        <Text>Formula: Attendance % = (Present + Excused) / Sessions × 100</Text>
      </View>
    </Page>
  </Document>
);

// PDF for weekly/term summary (with Mon–Fri ticks for weekly)
const AttendanceSummaryDoc = ({ cls, date, slot, rowsSummary, weekDates }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.header}>
        <Text style={pdfStyles.title}>Attendance Summary</Text>
        <Text style={pdfStyles.meta}>
          Class: {cls}   Date: {date}   Range: {slot}
          {weekDates?.length ? `   Week: ${weekDates[0]} – ${weekDates[weekDates.length - 1]}` : ""}
        </Text>
        <View style={pdfStyles.legend}>
          <Text>Legend: P=Present • A=Absent • L=Late • E=Excused  |  At-Risk &lt; 75% shown in red</Text>
        </View>
      </View>

      <View style={pdfStyles.row}>
        {[
          "#","Student Name","Adm No","Sess","P","A","L","E",
          ...(weekDates?.length ? weekDates.map(d => d.split(" ")[0]) : []),
          "%"
        ].map((h, i) => (
          <View
            key={i}
            style={[
              pdfStyles.cell,
              pdfStyles.th,
              { flex: [0.6, 2.2, 1.2, 0.8, 0.7, 0.7, 0.7, 0.7, ...(weekDates?.length ? weekDates.map(() => 0.7) : []), 0.9][i] }
            ]}
          >
            <Text>{h}</Text>
          </View>
        ))}
      </View>

      {rowsSummary.map((r, idx) => (
        <View key={r.id} style={pdfStyles.row}>
          <View style={[pdfStyles.cell, { flex: 0.6 }]}><Text>{idx + 1}</Text></View>
          <View style={[pdfStyles.cell, { flex: 2.2 }]}><Text>{r.name}</Text></View>
          <View style={[pdfStyles.cell, { flex: 1.2 }]}><Text>{r.admission_no}</Text></View>
          <View style={[pdfStyles.cell, { flex: 0.8 }]}><Text>{r.sessions}</Text></View>
          <View style={[pdfStyles.cell, { flex: 0.7 }]}><Text>{r.present}</Text></View>
          <View style={[pdfStyles.cell, { flex: 0.7 }]}><Text>{r.absent}</Text></View>
          <View style={[pdfStyles.cell, { flex: 0.7 }]}><Text>{r.late}</Text></View>
          <View style={[pdfStyles.cell, { flex: 0.7 }]}><Text>{r.excused}</Text></View>

          {weekDates?.length
            ? r.daily.map((st, i) => (
                <View key={i} style={[pdfStyles.cell, { flex: 0.7 }]}>
                  <Text>{st === 'present' ? 'P' : st === 'absent' ? 'A' : st === 'late' ? 'L' : 'E'}</Text>
                </View>
              ))
            : null}

          <View style={[pdfStyles.cell, { flex: 0.9 }]}>
            <Text style={{ color: r.attendancePct < 75 ? '#b91c1c' : '#111827' }}>{r.attendancePct}%</Text>
          </View>
        </View>
      ))}

      <View style={{ marginTop: 6 }}>
        <Text>Formula: (Present + Excused) / Sessions × 100</Text>
      </View>
    </Page>
  </Document>
);


export default function Attendance() {
  // Filters / header state
  const [mode, setMode] = useState("homeroom"); // homeroom | period
  const [classId, setClassId] = useState(dummyClasses[0].uuid);
  const [period, setPeriod] = useState("Morning");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [query, setQuery] = useState("");
  const [isOffline, setIsOffline] = useState(false);
  const [summaryRange, setSummaryRange] = useState("week"); // week | term

  // Working set of students for selected class
  const [rows, setRows] = useState(() => structuredClone(dummyStudents[dummyClasses[0].uuid]));

  useEffect(() => {
    // Simulate offline/online toggle every 25s (demo only)
    const t = setInterval(() => setIsOffline((x) => !x), 25000);
    return () => clearInterval(t);
  }, []);

  // When class changes, load its students (dummy)
  useEffect(() => {
    setRows(structuredClone(dummyStudents[classId] || []));
  }, [classId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((s) =>
      `${s.firstname} ${s.lastname}`.toLowerCase().includes(q) ||
      s.admission_no.toLowerCase().includes(q)
    );
  }, [rows, query]);

  const counters = useMemo(() => {
    const c = { present: 0, absent: 0, late: 0, excused: 0 };
    rows.forEach((r) => (c[r.status]++));
    return c;
  }, [rows]);

  // Derived data for PDF downloads
const clsName = useMemo(
  () => (dummyClasses.find((c) => c.uuid === classId)?.class_name) || "Class",
  [classId]
);

// Build summary rows + daily (Mon–Fri) ticks for weekly
const rowsSummaryPDF = useMemo(() => {
  const students = dummyStudents[classId] || [];
  const sessions = summaryRange === "term" ? 45 : 5;
  // generate a local history so the PDF has daily ticks too
  const pick = () => {
    const r = Math.random();
    if (r < 0.78) return STATUS.PRESENT;
    if (r < 0.86) return STATUS.LATE;
    if (r < 0.93) return STATUS.EXCUSED;
    return STATUS.ABSENT;
  };
  const hist = {};
  students.forEach((s) => (hist[s.id] = Array.from({ length: sessions }, pick)));

  return students.map((s) => {
    const h = hist[s.id] || [];
    const totals = h.reduce(
      (acc, st) => {
        acc.sessions++;
        if (st === STATUS.PRESENT) acc.present++;
        else if (st === STATUS.ABSENT) acc.absent++;
        else if (st === STATUS.LATE) acc.late++;
        else if (st === STATUS.EXCUSED) acc.excused++;
        return acc;
      },
      { sessions: 0, present: 0, absent: 0, late: 0, excused: 0 }
    );
    const attendancePct = pct(totals.present + totals.excused, totals.sessions);
    return {
      id: s.id,
      name: `${s.firstname} ${s.lastname}`,
      admission_no: s.admission_no,
      ...totals,
      attendancePct,
      //daily: h.slice(0, 5),
      daily: summaryRange === "week" ? h.slice(0, 5) : [],   // ← only in week mode
    };
  });
}, [classId, summaryRange]);

const weekDatesArr = useMemo(
  () => (summaryRange === "week" ? getWeekDates(date).map(d =>
    d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })
  ) : []),
  [summaryRange, date]
);


  // Weekly heatmap: % Present by weekday (Mon–Fri) for selected class (dummy history)
const weeklyHeat = useMemo(() => {
  if (summaryRange !== "week") return null;
  const students = dummyStudents[classId] || [];
  const hist = buildDummyHistory("week");
  const totals = Array.from({ length: 5 }).map(() => ({ present: 0, total: 0 }));
  students.forEach((s) => {
    const h = hist[s.id] || [];
    for (let i = 0; i < 5; i++) {
      const st = h[i];
      if (st) {
        totals[i].total++;
        if (st === STATUS.PRESENT) totals[i].present++;
      }
    }
  });
  return totals.map((t) => ({ pct: pct(t.present, t.total) }));
}, [classId, summaryRange, date]);



  const setStatus = (id, status) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const setNote = (id, note) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, note } : r)));
  };

  const markAll = (status) => {
    setRows((prev) => prev.map((r) => ({ ...r, status })));
  };

  const markRemainingAbsent = () => {
    setRows((prev) => prev.map((r) => (r.status === STATUS.PRESENT ? r : r.status ? r : r))); // noop safety
    setRows((prev) => prev.map((r) => (r.status ? r : { ...r, status: STATUS.ABSENT })));
  };

  const repeatYesterday = () => {
    // Demo: randomly mark 3 students late to simulate prior day
    setRows((prev) => {
      const copy = structuredClone(prev);
      copy.forEach((r) => (r.status = STATUS.PRESENT));
      for (let i = 0; i < Math.min(3, copy.length); i++) copy[i].status = STATUS.LATE;
      return copy;
    });
  };

  const fakeSave = () => {
    // In real app, post rows + metadata
    alert(
      `Saved ${rows.length} records for ${date} / ${mode === "homeroom" ? "Homeroom" : period} in ${dummyClasses.find((c) => c.uuid === classId)?.class_name}`
    );
  };

  // ---------------- SUMMARY (Dummy history) ----------------
  // Build a synthetic history of attendance statuses per student
  // for either the current week (5 school days) or a whole term (~9 weeks * 5 days = 45 sessions).
  function buildDummyHistory(range) {
    const students = dummyStudents[classId] || [];
    const sessions = range === "term" ? 45 : 5; // demo numbers
    // Weighted random toward Present; include small Late/Excused odds
    const pick = () => {
      const r = Math.random();
      if (r < 0.78) return STATUS.PRESENT;
      if (r < 0.86) return STATUS.LATE;
      if (r < 0.93) return STATUS.EXCUSED;
      return STATUS.ABSENT;
    };
    const hist = {};
    students.forEach((s) => {
      hist[s.id] = Array.from({ length: sessions }, pick);
    });
    return hist;
  };

  const getSummaryData = () => {
    const students = dummyStudents[classId] || [];
    const hist = buildDummyHistory(summaryRange);
    return students.map((s) => {
      const h = hist[s.id] || [];
      const totals = h.reduce(
        (acc, st) => {
          acc.sessions++;
          if (st === STATUS.PRESENT) acc.present++;
          else if (st === STATUS.ABSENT) acc.absent++;
          else if (st === STATUS.LATE) acc.late++;
          else if (st === STATUS.EXCUSED) acc.excused++;
          return acc;
        },
        { sessions: 0, present: 0, absent: 0, late: 0, excused: 0 }
      );
      const attendancePct = pct(totals.present + totals.excused, totals.sessions);
      return {
        id: s.id,
        name: `${s.firstname} ${s.lastname}`,
        admission_no: s.admission_no,
        ...totals,
        attendancePct,
      };
    });
  };

  const exportSummaryCSV = () => {
    const cls = dummyClasses.find((c) => c.uuid === classId)?.class_name || "Class";
    const summary = getSummaryData();
    const header = [
      "Student Name",
      "Admission No",
      "Days",
      "Present",
      "Absent",
      "Late",
      "Excused",
      "Attendance %",
      "At-Risk (<75%)",
    ];
    const lines = summary.map((r) => [
      r.name,
      r.admission_no,
      r.sessions,
      r.present,
      r.absent,
      r.late,
      r.excused,
      r.attendancePct,
      r.attendancePct < 75 ? "YES" : "",
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slot = summaryRange === "week" ? "weekly" : "term";
    a.href = url;
    a.download = `attendance_summary_${cls}_${date}_${slot}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportSummaryPDF = () => {
  const cls = dummyClasses.find((c) => c.uuid === classId)?.class_name || "Class";
  const slot = summaryRange === "week" ? "Weekly Summary" : "Term Summary";

  // Build fresh dummy history for export (so we have daily marks)
  const hist = buildDummyHistory(summaryRange);
  const students = dummyStudents[classId] || [];

  // Summaries (also store first 5 daily states for weekly view)
  const rowsSummary = students.map((s) => {
    const h = hist[s.id] || [];
    const totals = h.reduce(
      (acc, st) => {
        acc.sessions++;
        if (st === STATUS.PRESENT) acc.present++;
        else if (st === STATUS.ABSENT) acc.absent++;
        else if (st === STATUS.LATE) acc.late++;
        else if (st === STATUS.EXCUSED) acc.excused++;
        return acc;
      },
      { sessions: 0, present: 0, absent: 0, late: 0, excused: 0 }
    );
    const attendancePct = pct(totals.present + totals.excused, totals.sessions);
    return {
      id: s.id,
      name: `${s.firstname} ${s.lastname}`,
      admission_no: s.admission_no,
      ...totals,
      attendancePct,
      daily: h.slice(0, 5),
    };
  });

  const weekDates = summaryRange === "week" ? getWeekDates(date) : [];
  const weekRangeLabel = summaryRange === "week"
    ? `${fmtDay(weekDates[0])} – ${fmtDay(weekDates[weekDates.length - 1])}`
    : "";

  const thDays = summaryRange === "week"
    ? weekDates.map((d) => `<th class="day">${d.toLocaleDateString(undefined, { weekday: 'short' })}</th>`).join("")
    : "";

  const toBadge = (st) => {
    const code = st === STATUS.PRESENT ? "P" : st === STATUS.ABSENT ? "A" : st === STATUS.LATE ? "L" : "E";
    return `<span class="cell-badge ${code}">${code}</span>`;
  };

  const tableRows = rowsSummary
    .map((r, i) => {
      const risk = r.attendancePct < 75; // threshold
      const dayCells = summaryRange === "week" ? r.daily.map((st) => `<td class="day">${toBadge(st)}</td>`).join("") : "";
      return `
        <tr>
          <td>${i + 1}</td>
          <td>${r.name}</td>
          <td>${r.admission_no}</td>
          <td>${r.sessions}</td>
          <td>${r.present}</td>
          <td>${r.absent}</td>
          <td>${r.late}</td>
          <td>${r.excused}</td>
          ${dayCells}
          <td class="${risk ? 'risk' : ''}">${r.attendancePct}%</td>
        </tr>`;
    })
    .join("");

  const legend = `
    <div class="legend">
      <span class="chip present"></span> Present
      <span class="chip late"></span> Late
      <span class="chip excused"></span> Excused
      <span class="chip absent"></span> Absent
      <span class="sep">|</span>
      <span class="risk-note">At-Risk &lt; 75% highlighted in red</span>
    </div>`;

  const win = window.open("", "_blank", "noopener,noreferrer");
  if (!win) return alert("Popup blocked. Please allow popups for this site.");

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Attendance Summary - ${cls}</title>
  <style>
    body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:#111827; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom: 12px; }
    .title { font-weight:700; font-size:18px; }
    .meta { font-size:12px; color:#374151; text-align:right; }
    .legend { font-size:11px; color:#374151; display:flex; align-items:center; gap:8px; margin-bottom:10px; }
    .chip { display:inline-block; width:10px; height:10px; border-radius:2px; margin-right:4px; vertical-align:middle; }
    .present { background:#86efac; }
    .late { background:#fde68a; }
    .excused { background:#93c5fd; }
    .absent { background:#fca5a5; }
    .sep { margin: 0 6px; color:#9ca3af; }
    .risk { color:#b91c1c; font-weight:700; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th, td { border:1px solid #e5e7eb; padding:8px; text-align:left; }
    th { background:#f9fafb; }
    th.day, td.day { text-align:center; width: 26px; }
    .cell-badge { display:inline-block; min-width:16px; padding:2px 4px; border-radius:3px; font-weight:700; font-size:10px; color:#111827; }
    .cell-badge.P { background:#86efac; }
    .cell-badge.A { background:#fca5a5; }
    .cell-badge.L { background:#fde68a; }
    .cell-badge.E { background:#93c5fd; }
    .footer { margin-top: 12px; display:flex; justify-content:space-between; font-size:12px; color:#374151; }
    @media print { @page { size: A4 portrait; margin: 16mm; } .no-print { display:none; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">Attendance Summary</div>
      ${legend}
    </div>
    <div class="meta">
      Class: <b>${cls}</b><br/>
      Date: <b>${date}</b><br/>
      Range: <b>${slot}</b><br/>
      ${summaryRange === "week" ? `Week: <b>${fmtDay(weekDates[0])} – ${fmtDay(weekDates[weekDates.length - 1])}</b>` : ''}
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Student Name</th>
        <th>Admission No</th>
        <th>Days</th>
        <th>Present</th>
        <th>Absent</th>
        <th>Late</th>
        <th>Excused</th>
        ${thDays}
        <th>Attendance %</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>
  <div class="footer">
    <div>Generated: ${new Date().toLocaleString()}</div>
    <div>Formula: (Present + Excused) / Days × 100</div>
  </div>
  <div class="no-print" style="margin-top:12px;">
    <button onclick="window.print()" style="padding:8px 12px;">Print</button>
  </div>
</body>
</html>`;

  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 300);
};


  const exportCSV = () => {
    const cls = dummyClasses.find((c) => c.uuid === classId)?.class_name || "Class";
    const header = ["#", "First Name", "Last Name", "Admission No", "Status", "Note"];
    const lines = rows.map((s, i) => [
      i + 1,
      s.firstname,
      s.lastname,
      s.admission_no,
      statusMeta[s.status]?.label || s.status,
      s.note || "",
    ]);
    const csv = [header, ...lines]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slot = mode === "homeroom" ? "homeroom" : `period_${period}`;
    a.href = url;
    a.download = `attendance_${cls}_${date}_${slot}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    // No external libs: open a print-friendly window and trigger print
    const cls = dummyClasses.find((c) => c.uuid === classId)?.class_name || "Class";
    const slot = mode === "homeroom" ? "Homeroom" : `Period: ${period}`;
    const win = window.open("", "_blank", "noopener,noreferrer");
    if (!win) return alert("Popup blocked. Please allow popups for this site.");

    const tableRows = rows
      .map((s, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${s.firstname} ${s.lastname}</td>
          <td>${s.admission_no}</td>
          <td>${statusMeta[s.status]?.label || s.status}</td>
          <td>${(s.note || "").replace(/</g, "&lt;")}</td>
        </tr>`)
      .join("");

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Attendance ${cls} - ${date}</title>
  <style>
    body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:#111827; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom: 16px; }
    .title { font-weight:700; font-size:18px; }
    .meta { font-size:12px; color:#374151; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th, td { border:1px solid #e5e7eb; padding:8px; text-align:left; }
    th { background:#f9fafb; }
    .footer { margin-top: 16px; display:flex; justify-content:space-between; font-size:12px; color:#374151; }
    @media print {
      @page { size: A4 portrait; margin: 16mm; }
      .no-print { display:none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="title">Attendance Register</div>
    <div class="meta">
      Class: <b>${cls}</b><br/>
      Date: <b>${date}</b><br/>
      Slot: <b>${slot}</b>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Student Name</th>
        <th>Admission No</th>
        <th>Status</th>
        <th>Note</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>
  <div class="footer">
    <div>Present: ${rows.filter(r=>r.status==='present').length} • Absent: ${rows.filter(r=>r.status==='absent').length} • Late: ${rows.filter(r=>r.status==='late').length} • Excused: ${rows.filter(r=>r.status==='excused').length}</div>
    <div>Generated: ${new Date().toLocaleString()}</div>
  </div>
  <div class="no-print" style="margin-top:12px;">
    <button onclick="window.print()" style="padding:8px 12px;">Print</button>
  </div>
</body>
</html>`;

    win.document.write(html);
    win.document.close();
    // Some browsers need a short delay
    setTimeout(() => win.print(), 300);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header / Context */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-2">
            <NotebookPen className="w-5 h-5 text-indigo-600" />
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Take Attendance
            </h1>
            {isOffline && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                <CloudOff className="w-3.5 h-3.5" /> Offline (will sync)
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            {/* Mode */}
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-1 bg-gray-50 dark:bg-gray-700/40">
              <button
                onClick={() => setMode("homeroom")}
                className={`px-3 py-1.5 rounded-md text-sm ${
                  mode === "homeroom"
                    ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-300"
                }`}
              >
                Homeroom
              </button>
              <button
                onClick={() => setMode("period")}
                className={`px-3 py-1.5 rounded-md text-sm ${
                  mode === "period"
                    ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-300"
                }`}
              >
                Period
              </button>
            </div>

            {/* Class */}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-500" />
              <select
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {dummyClasses.map((c) => (
                  <option key={c.uuid} value={c.uuid}>
                    {c.class_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Period (if in period mode) */}
            {mode === "period" && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {[
                    "Morning",
                    "Midday",
                    "Afternoon",
                    "After-school",
                  ].map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tools bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2 w-full md:w-96">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or admission no."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <button className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40">
              <Filter className="w-4 h-4" /> Filters
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => markAll(STATUS.PRESENT)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-green-200 text-green-700 bg-green-50 hover:bg-green-100"
            >
              <CheckCircle2 className="w-4 h-4" /> All Present
            </button>
            <button
              onClick={markRemainingAbsent}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
            >
              <XCircle className="w-4 h-4" /> Mark Remaining Absent
            </button>
            <button
              onClick={repeatYesterday}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
            >
              <RefreshCw className="w-4 h-4" /> Repeat Yesterday
            </button>
            <div className="hidden md:block w-px h-6 bg-gray-200 dark:bg-gray-700" />
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <PDFDownloadLink
                key={`register-${classId}-${date}-${mode}-${period}`}
                document={
                    <AttendanceRegisterDoc
                    cls={clsName}
                    date={date}
                    slot={mode === 'homeroom' ? 'Homeroom' : `Period: ${period}`}
                    rows={rows}
                    statusMeta={statusMeta}
                    />
                }
                fileName={`attendance_${clsName}_${date}_${mode==='homeroom'?'homeroom':`period_${period}`}.pdf`}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100"
                >
                <Printer className="w-4 h-4" /> Download Register PDF
            </PDFDownloadLink>


          </div>
        </div>

        {/* Summary export controls */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-300">Summary range:</span>
            <select
              value={summaryRange}
              onChange={(e) => setSummaryRange(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="week">This Week (5 days)</option>
              <option value="term">This Term (demo)</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportSummaryCSV}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100"
            >
              <Download className="w-4 h-4" /> Export Summary CSV
            </button>
            {summaryRange && (
            <PDFDownloadLink
                key={`summary-${summaryRange}-${classId}-${date}`}
                document={
                    <AttendanceSummaryDoc
                    cls={clsName}
                    date={date}
                    slot={summaryRange==='week' ? 'Weekly Summary' : 'Term Summary'}
                    rowsSummary={rowsSummaryPDF}
                    weekDates={weekDatesArr}
                    />
                }
                fileName={`attendance_summary_${clsName}_${date}_${summaryRange==='week'?'weekly':'term'}.pdf`}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100"
                >
                <Printer className="w-4 h-4" /> Download Summary PDF
            </PDFDownloadLink>
            )}

          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* List */}
        <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <ListChecks className="w-4 h-4" />
              <span>
                {filtered.length} of {rows.length} students
              </span>
            </div>
            <div className="text-xs text-gray-500">Tap a status chip to toggle</div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filtered.map((s, idx) => (
              <div
                key={s.id}
                className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center text-sm font-semibold text-gray-600 dark:text-gray-200">
                    {idx + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      {s.firstname} {s.lastname}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{s.admission_no}</div>
                  </div>
                </div>

                {/* Status chips */}
                <div className="flex flex-wrap items-center gap-2">
                  {Object.values(STATUS).map((st) => (
                    <button
                      key={st}
                      onClick={() => setStatus(s.id, st)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                        s.status === st
                          ? statusMeta[st].cls + " ring-1 ring-offset-1 ring-offset-white dark:ring-offset-gray-800"
                          : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      }`}
                    >
                      {statusMeta[st].label}
                    </button>
                  ))}
                </div>

                {/* Optional note */}
                <div className="w-full sm:w-64">
                  <input
                    value={s.note}
                    onChange={(e) => setNote(s.id, e.target.value)}
                    placeholder="Note (optional)"
                    className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar summary */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 sticky top-4">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-indigo-600" /> Summary
              </h3>
            </div>
            <div className="p-4 sm:p-6 space-y-3">
              <Row label="Present" value={counters.present} badgeCls="bg-green-100 text-green-800" />
              <Row label="Absent" value={counters.absent} badgeCls="bg-red-100 text-red-800" />
              <Row label="Late" value={counters.late} badgeCls="bg-yellow-100 text-yellow-800" />
              <Row label="Excused" value={counters.excused} badgeCls="bg-blue-100 text-blue-800" />

              <div className="text-xs text-gray-500 border-t pt-3 mt-3">
                {mode === "homeroom" ? (
                  <span>Mode: Homeroom • Date: {date}</span>
                ) : (
                  <span>
                    Mode: Period • {period} • Date: {date}
                  </span>
                )}
              </div>

              <button
                onClick={fakeSave}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
              >
                <CheckCircle2 className="w-4 h-4" /> Save Attendance
              </button>

              <button
                onClick={() => alert("Changes discarded (demo)")}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                <XCircle className="w-4 h-4" /> Discard Changes
              </button>

              {summaryRange === 'week' && weeklyHeat && (
                <div className="rounded-lg p-3 text-xs bg-gray-50 dark:bg-gray-700/40 text-gray-700 dark:text-gray-200">
                    <div className="mb-2 font-semibold">Class Heatmap (Mon–Fri)</div>
                    <div className="grid grid-cols-5 gap-1">
                    {weeklyHeat.map((d, i) => (
                        <div
                        key={i}
                        className="rounded-md p-2 text-center"
                        style={{ backgroundColor: colorFromPct(d.pct) }}
                        >
                        <div className="font-semibold leading-none text-[11px]">{["Mon","Tue","Wed","Thu","Fri"][i]}</div>
                        <div className="text-[11px]">{d.pct}%</div>
                        </div>
                    ))}
                    </div>
                </div>
              )}


              <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 text-xs text-gray-600 dark:text-gray-300 flex items-start gap-2">
                <AlertIcon />
                <p>
                  Tip: Use <b>All Present</b> then change just a few. That’s the fastest flow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, badgeCls }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-700 dark:text-gray-200">{label}</span>
      <span className={`inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded-full text-xs font-semibold ${badgeCls}`}>
        {value}
      </span>
    </div>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 mt-0.5">
      <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.7-3L13.7 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
